/**
 * GameUITheme - Shared design tokens and UI helpers for in-game HUD
 * Matches the visual language of the login/pre-game screens:
 *   Orbitron headings, Share Tech Mono body, cyan primary, frosted panels
 */

import Phaser from 'phaser';
import { getHeadingFont, getBodyFont, getUIFont } from '../../i18n/fontManager';
import { resolveDialogSize } from '../../utils/ResponsiveHelper';
import { getSafeAreaInsets } from '../../utils/SafeArea';

// ── Color Palette ──────────────────────────────────────────────
export const Colors = {
  // Primary
  cyan:        0x00ffff,
  cyanDim:     0x338899,
  cyanMuted:   0x1a6666,

  // Accents
  green:       0x00ff88,
  gold:        0xffcc00,
  red:         0xff4444,
  redDim:      0xaa3333,
  orange:      0xffaa44,
  purple:      0x8855cc,

  // Backgrounds (dark → light)
  bgDeep:      0x030a16,
  bgDark:      0x0a0f1e,
  bgPanel:     0x0c1627,
  bgPanelLit:  0x122038,
  bgElevated:  0x162a4a,
  bgCard:      0x162040,
  bgCardHover: 0x1e2e55,

  // Borders
  borderDim:   0x1a3355,
  borderMed:   0x2a4a7a,
  borderBright:0x3a6aaa,

  // Text (as hex numbers for Phaser .setTint / fill)
  textWhite:   0xffffff,
  textLight:   0xa0c0d8,
  textMuted:   0x6888a8,
  textDark:    0x445566,
} as const;

// CSS color strings for Phaser text style objects
export const CSSColors = {
  cyan:      '#00ffff',
  cyanDim:   '#338899',
  green:     '#00ff88',
  gold:      '#ffcc00',
  red:       '#ff6666',
  redBright: '#ff4444',
  orange:    '#ffaa44',
  purple:    '#cc88ff',

  textWhite: '#ffffff',
  textLight: '#a0c0d8',
  textMuted: '#6888a8',
  textDim:   '#445566',
  textCyan:  '#88ddff',
  textGold:  '#ffee88',
  headerLight: '#E0E8FF',
  statLabel: '#8899BB',
} as const;

// ── Typography (locale-aware via getters for CJK font support) ──
export const Fonts: { readonly heading: string; readonly body: string; readonly ui: string } = {
  get heading() { return getHeadingFont(); },
  get body() { return getBodyFont(); },
  get ui() { return getUIFont(); },
};

export const FontSize = {
  xs:    '12px',
  sm:    '14px',
  md:    '16px',
  lg:    '18px',
  xl:    '20px',
  xxl:   '24px',
  title: '28px',
} as const;

// ── Spacing ────────────────────────────────────────────────────
export const Space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
} as const;

// ── Borders & Radii ────────────────────────────────────────────
export const Radius = {
  sm: 4,
  md: 6,
  lg: 8,
  xl: 12,
} as const;

// ── Animation ──────────────────────────────────────────────────
export const Anim = {
  fast:    120,
  normal:  200,
  slow:    350,
  panel:   300,
  dialog:  250,
  ease:    'Power2',
  easeOut: 'Back.easeOut',
} as const;

// ── Z-Index Layers ─────────────────────────────────────────────
export const Depth = {
  panel:    1000,
  topBar:   1100,
  overlay:  2000,
  dialog:   2500,
  tooltip:  3000,
} as const;


// ── Nav Button Color Palette (uniform dark bg, grouped accent text) ──
// All buttons share the same dark indigo background; color category is text-only
const NAV_BG      = 0x1C1F45;
const NAV_BG_HOVER = 0x2A2F60;

export const NavColors: Record<string, { bg: number; bgHover: number; text: string }> = {
  wreck:   { bg: NAV_BG, bgHover: NAV_BG_HOVER, text: '#00ffff' },   // cyan
  trade:   { bg: NAV_BG, bgHover: NAV_BG_HOVER, text: '#00ff41' },   // green
  ore:     { bg: NAV_BG, bgHover: NAV_BG_HOVER, text: '#00ff41' },   // green
  crew:    { bg: NAV_BG, bgHover: NAV_BG_HOVER, text: '#66aaff' },   // blue
  parts:   { bg: NAV_BG, bgHover: NAV_BG_HOVER, text: '#66aaff' },   // blue
  shop:    { bg: NAV_BG, bgHover: NAV_BG_HOVER, text: '#ff00ff' },   // magenta
  station: { bg: NAV_BG, bgHover: NAV_BG_HOVER, text: '#ff00ff' },   // magenta
  center:  { bg: NAV_BG, bgHover: NAV_BG_HOVER, text: '#ffff00' },   // yellow
  insure:  { bg: NAV_BG, bgHover: NAV_BG_HOVER, text: '#66aaff' },   // blue (same group as crew/parts)
  refund:  { bg: NAV_BG, bgHover: NAV_BG_HOVER, text: '#dd8866' },
  hq:      { bg: NAV_BG, bgHover: NAV_BG_HOVER, text: '#aa77dd' },
} as const;

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// Helper functions – draw consistent UI elements with Phaser Graphics
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/**
 * Draw a shark-fin shaped panel (left side - wider at top, tapers on the right toward bottom)
 */
export function drawSharkFinPanel(
  g: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number, h: number,
  opts: { taper?: number; alpha?: number; borderColor?: number; borderAlpha?: number } = {},
): void {
  const taper = opts.taper ?? 30;
  const alpha = opts.alpha ?? 0.82;
  const border = opts.borderColor ?? Colors.borderMed;
  const bAlpha = opts.borderAlpha ?? 0.4;
  const r = Radius.lg;

  // Main fill - tapered polygon with rounded top
  g.fillStyle(Colors.bgPanel, alpha);
  g.beginPath();
  g.moveTo(x + r, y);
  g.lineTo(x + w - r, y);
  g.arc(x + w - r, y + r, r, -Math.PI / 2, 0);      // top-right corner
  g.lineTo(x + w - taper, y + h - r);                  // right edge tapers inward
  g.arc(x + w - taper - r, y + h - r, r, 0, Math.PI / 2); // bottom-right corner
  g.lineTo(x + r, y + h);
  g.arc(x + r, y + h - r, r, Math.PI / 2, Math.PI);   // bottom-left corner
  g.lineTo(x, y + r);
  g.arc(x + r, y + r, r, Math.PI, -Math.PI / 2);      // top-left corner
  g.closePath();
  g.fillPath();

  // Border
  g.lineStyle(1.5, border, bAlpha);
  g.beginPath();
  g.moveTo(x + r, y);
  g.lineTo(x + w - r, y);
  g.arc(x + w - r, y + r, r, -Math.PI / 2, 0);
  g.lineTo(x + w - taper, y + h - r);
  g.arc(x + w - taper - r, y + h - r, r, 0, Math.PI / 2);
  g.lineTo(x + r, y + h);
  g.arc(x + r, y + h - r, r, Math.PI / 2, Math.PI);
  g.lineTo(x, y + r);
  g.arc(x + r, y + r, r, Math.PI, -Math.PI / 2);
  g.closePath();
  g.strokePath();

  // Top glow line
  g.lineStyle(1, Colors.cyan, 0.1);
  g.beginPath();
  g.moveTo(x + r + 4, y);
  g.lineTo(x + w - r - 4, y);
  g.strokePath();
}

/**
 * Draw a shark-fin shaped panel for the RIGHT side.
 * Based on Figma "Rectangle 9" vector path (rotated 180°):
 *   Original path: M0 0 L103 0 C180.32 0 243 62.68 243 140 L243 508 L0 508 Z
 *   Rotated 180°: the curved fin appears at the top-left of the right panel.
 *
 * The shape is a rectangle with a smooth curved cutout at the top-left corner,
 * where the left edge starts at y=finCurveH and curves up to the top edge.
 */
export function drawSharkFinRight(
  g: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number, h: number,
  opts: { fillColor?: number; fillAlpha?: number;
          borderColor?: number; borderAlpha?: number; borderWidth?: number } = {},
): void {
  const fill = opts.fillColor ?? 0x131026;
  const fAlpha = opts.fillAlpha ?? 1.0;
  const border = opts.borderColor ?? Colors.cyan;
  const bAlpha = opts.borderAlpha ?? 0.15;
  const bWidth = opts.borderWidth ?? 3;

  // Figma original path (unrotated, 243x508):
  //   M0,0 L103,0 C180.32,0 243,62.68 243,140 L243,508 L0,508 Z
  // Rotated 180° around center (121.5, 254):
  //   bottom-right gets the curve, top-left is square corner
  // But visually the fin is at the top-left, so we mirror:
  //   The right panel fin: left edge has a concave curve from (x, y+h) up,
  //   the top-left corner curves outward.
  //
  // For the right panel, reading the relativeTransform confirms it's mirrored horizontally.
  // Mirrored path (flipped on X axis within w):
  //   Start at top-right, go left along top, curve down on the left side.
  //
  // Final shape (right panel, fin on left side):
  //   Top edge: full width (x, y) to (x+w, y)
  //   Right edge: straight down (x+w, y) to (x+w, y+h)
  //   Bottom edge: (x+w, y+h) to (x + w-103, y+h)  -- note: 103 is the "straight" portion before curve
  //   Curve: from (x + w-103, y+h) curving to (x, y+h-140)
  //   Left edge: (x, y+h-140) to (x, y)
  //
  // WAIT - re-reading the Figma: the transform is [[-1,0,1410],[0,1,81]] which means
  // it's reflected horizontally (scale x = -1). The original shape has the curve at top-right.
  // Reflected: curve moves to top-left. So:
  //   Start at (x+w, y)  -- top-right (square corner)
  //   Line to (x + (w-103), y)  -- along top edge, stop before curve
  //   Curve from (x + (w-103), y) to (x, y+140)  -- bezier going left and down
  //   Line to (x, y+h)  -- left edge down
  //   Line to (x+w, y+h)  -- bottom edge
  //   Close back to (x+w, y)  -- right edge up

  const curveStartX = w - 103;   // where straight top edge ends and curve begins
  const curveEndY = 140;         // where curve meets the left edge

  // Fill
  g.fillStyle(fill, fAlpha);
  g.beginPath();
  g.moveTo(x + w, y);                             // top-right corner
  g.lineTo(x + curveStartX, y);                   // along top edge to curve start
  // Cubic bezier: curve from (curveStartX, 0) down to (0, curveEndY)
  // Original Figma control points: C180.32,0 243,62.68 243,140 (before mirror)
  // Mirrored: C(w-180.32, 0) (x, 62.68) -> (x, 140)
  const cp1x = x + (w - 180.32);                  // first control point X
  const cp1y = y;                                  // first control point Y (on top edge)
  const cp2x = x;                                  // second control point X (on left edge)
  const cp2y = y + 62.68;                          // second control point Y
  // Use quadratic approximation since Phaser Graphics doesn't have cubic bezier
  // We'll use multiple line segments to approximate the curve
  const steps = 30;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    // Cubic bezier: B(t) = (1-t)^3*P0 + 3(1-t)^2*t*P1 + 3(1-t)*t^2*P2 + t^3*P3
    const px = mt3 * (x + curveStartX) + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * x;
    const py = mt3 * y + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * (y + curveEndY);
    g.lineTo(px, py);
  }
  g.lineTo(x, y + h);                             // left edge down to bottom-left
  g.lineTo(x + w, y + h);                         // bottom edge to bottom-right
  g.closePath();
  g.fillPath();

  // Border stroke
  g.lineStyle(bWidth, border, bAlpha);
  g.beginPath();
  g.moveTo(x + w, y);
  g.lineTo(x + curveStartX, y);
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    const px = mt3 * (x + curveStartX) + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * x;
    const py = mt3 * y + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * (y + curveEndY);
    g.lineTo(px, py);
  }
  g.lineTo(x, y + h);
  g.lineTo(x + w, y + h);
  g.closePath();
  g.strokePath();
}

/**
 * Draw a shark-fin shaped panel for the LEFT side.
 * Mirror of drawSharkFinRight: concave cubic bezier curve at the top-RIGHT corner.
 * Original Figma path: M0,0 L103,0 C180.32,0 243,62.68 243,140 L243,H L0,H Z
 */
export function drawSharkFinLeft(
  g: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number, h: number,
  opts: { fillColor?: number; fillAlpha?: number;
          borderColor?: number; borderAlpha?: number; borderWidth?: number } = {},
): void {
  const fill = opts.fillColor ?? 0x131026;
  const fAlpha = opts.fillAlpha ?? 1.0;
  const border = opts.borderColor ?? Colors.cyan;
  const bAlpha = opts.borderAlpha ?? 0.15;
  const bWidth = opts.borderWidth ?? 3;

  // Curve from (103, 0) to (w, 140) — concave scoop at top-right
  const curveStartX = 103;   // where straight top edge ends
  const curveEndY = 140;     // where curve meets the right edge

  // Cubic bezier control points (from Figma vector)
  const cp1x = x + 180.32;
  const cp1y = y;
  const cp2x = x + w;
  const cp2y = y + 62.68;

  const steps = 30;

  // Fill
  g.fillStyle(fill, fAlpha);
  g.beginPath();
  g.moveTo(x, y);                              // top-left corner (square)
  g.lineTo(x + curveStartX, y);                // along top edge to curve start
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    const px = mt3 * (x + curveStartX) + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * (x + w);
    const py = mt3 * y + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * (y + curveEndY);
    g.lineTo(px, py);
  }
  g.lineTo(x + w, y + h);                      // right edge down to bottom-right
  g.lineTo(x, y + h);                          // bottom edge to bottom-left
  g.closePath();
  g.fillPath();

  // Border stroke
  g.lineStyle(bWidth, border, bAlpha);
  g.beginPath();
  g.moveTo(x, y);
  g.lineTo(x + curveStartX, y);
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    const px = mt3 * (x + curveStartX) + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * (x + w);
    const py = mt3 * y + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * (y + curveEndY);
    g.lineTo(px, py);
  }
  g.lineTo(x + w, y + h);
  g.lineTo(x, y + h);
  g.closePath();
  g.strokePath();
}

/**
 * Draw a frosted-glass panel (matches login form aesthetic)
 */
export function drawPanel(
  g: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number, h: number,
  opts: { alpha?: number; radius?: number; borderColor?: number; borderAlpha?: number; glowEdge?: boolean } = {},
): void {
  const alpha    = opts.alpha ?? 0.75;
  const radius   = opts.radius ?? Radius.lg;
  const border   = opts.borderColor ?? Colors.borderMed;
  const bAlpha   = opts.borderAlpha ?? 0.5;

  // Main fill
  g.fillStyle(Colors.bgPanel, alpha);
  g.fillRoundedRect(x, y, w, h, radius);

  // Subtle inner glow on left edge
  g.fillStyle(Colors.bgElevated, 0.15);
  g.fillRoundedRect(x, y, Math.min(30, w * 0.1), h, { tl: radius, bl: radius, tr: 0, br: 0 });

  // Border
  g.lineStyle(1.5, border, bAlpha);
  g.strokeRoundedRect(x, y, w, h, radius);

  // Optional bright glow edge on top
  if (opts.glowEdge !== false) {
    g.lineStyle(1, Colors.cyan, 0.12);
    g.beginPath();
    g.arc(x + radius, y + radius, radius, Math.PI, Math.PI * 1.5);
    g.lineTo(x + w - radius, y);
    g.arc(x + w - radius, y + radius, radius, Math.PI * 1.5, Math.PI * 2);
    g.strokePath();
  }
}

/**
 * Draw a panel header / title bar
 */
export function drawPanelHeader(
  g: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number, h: number,
  opts: { radius?: number } = {},
): void {
  const r = opts.radius ?? Radius.lg;
  g.fillStyle(Colors.bgPanelLit, 0.6);
  g.fillRoundedRect(x, y, w, h, { tl: r, tr: r, bl: 0, br: 0 });
  // Bottom accent line
  g.lineStyle(1, Colors.cyan, 0.2);
  g.lineBetween(x + Space.sm, y + h, x + w - Space.sm, y + h);
}

/**
 * Create a styled HUD button container (consistent across all top-bar / panel buttons)
 */
export function createButton(
  scene: Phaser.Scene,
  x: number, y: number,
  label: string,
  opts: {
    icon?: string;
    width?: number;
    height?: number;
    shortcut?: string;
    onClick?: () => void;
    depth?: number;
  } = {},
): Phaser.GameObjects.Container {
  const w = opts.width ?? 80;
  const h = opts.height ?? 34;

  const container = scene.add.container(x, y);

  // Background
  const bg = scene.add.graphics();
  drawButtonBg(bg, 0, 0, w, h, false);
  container.add(bg);

  // Icon (if provided)
  let textX = w / 2;
  if (opts.icon) {
    const iconText = scene.add.text(Space.md, h / 2, opts.icon, { fontSize: '14px' });
    iconText.setOrigin(0, 0.5);
    container.add(iconText);
    textX = w / 2 + 6;
  }

  // Label
  const text = scene.add.text(textX, h / 2, label, {
    fontSize: FontSize.sm,
    color: CSSColors.textLight,
    fontFamily: Fonts.body,
  });
  text.setOrigin(0.5);
  container.add(text);

  // Shortcut hint (tiny, bottom-right)
  if (opts.shortcut) {
    const hint = scene.add.text(w - 4, h - 2, opts.shortcut, {
      fontSize: '9px',
      color: CSSColors.textDim,
      fontFamily: Fonts.body,
    });
    hint.setOrigin(1, 1);
    container.add(hint);
  }

  // Invisible hit zone for reliable input (avoids Container.setInteractive issues in nested containers)
  const hitZone = scene.add.zone(0, 0, w, h).setOrigin(0, 0);
  hitZone.setInteractive(
    new Phaser.Geom.Rectangle(0, 0, w, h),
    Phaser.Geom.Rectangle.Contains,
  );
  container.add(hitZone);

  container.setDepth(opts.depth ?? Depth.topBar);

  // Hover / press states (on the hit zone, not the container)
  hitZone.on('pointerover', () => {
    bg.clear();
    drawButtonBg(bg, 0, 0, w, h, true);
    text.setColor(CSSColors.textWhite);
    scene.input.setDefaultCursor('pointer');
  });

  hitZone.on('pointerout', () => {
    bg.clear();
    drawButtonBg(bg, 0, 0, w, h, false);
    text.setColor(CSSColors.textLight);
    scene.input.setDefaultCursor('default');
  });

  hitZone.on('pointerdown', () => {
    // Press feedback: quick scale dip
    scene.tweens.add({
      targets: container,
      scaleX: 0.94,
      scaleY: 0.94,
      duration: 60,
      yoyo: true,
      ease: 'Quad.easeOut',
    });
    opts.onClick?.();
  });

  return container;
}

/**
 * Create a compact nav button for the top bar.
 * Supports image sprite icons (iconKey) or text fallback (icon).
 * Sharp rectangles (no corner radius) matching Figma design.
 */
export function createPillButton(
  scene: Phaser.Scene,
  x: number, y: number,
  label: string,
  opts: {
    icon?: string;          // text/emoji fallback icon
    iconKey?: string;       // Phaser texture key for image sprite icon
    colorKey?: string;      // key into NavColors
    accentColor?: number;   // override bg color
    width?: number;
    height?: number;
    onClick?: () => void;
    depth?: number;
    labelOffsetX?: number;  // extra px to shift label right
  } = {},
): Phaser.GameObjects.Container {
  const w = opts.width ?? 72;
  const h = opts.height ?? 24;
  const nc = opts.colorKey ? NavColors[opts.colorKey] : undefined;
  const bgColor     = nc?.bg     ?? opts.accentColor ?? Colors.bgPanelLit;
  const bgHover     = nc?.bgHover ?? (bgColor + 0x222222);
  const textColor   = nc?.text    ?? CSSColors.textLight;

  const container = scene.add.container(x, y);

  // Background rectangle (no corner radius)
  const bg = scene.add.graphics();
  drawNavRect(bg, 0, 0, w, h, bgColor, 0.85);
  container.add(bg);

  // Icon: prefer image sprite (iconKey) over text fallback
  const hasIcon = !!(opts.iconKey || opts.icon);
  const iconLeftPad = 8;       // padding before icon
  const iconSize = 12;         // icon display size
  const iconGap = 6;           // gap between icon and label
  const iconEndX = iconLeftPad + iconSize + iconGap; // where label area starts

  if (opts.iconKey && scene.textures.exists(opts.iconKey)) {
    const iconImg = scene.add.image(iconLeftPad + iconSize / 2, h / 2, opts.iconKey);
    iconImg.setDisplaySize(iconSize, iconSize);
    iconImg.setOrigin(0.5, 0.5);
    container.add(iconImg);
  } else if (opts.icon) {
    const iconText = scene.add.text(iconLeftPad, h / 2, opts.icon, {
      fontSize: '12px',
      color: textColor,
    });
    iconText.setOrigin(0, 0.5);
    container.add(iconText);
  }

  // Label - uppercase, compact Orbitron Black, 10px
  // Center label between icon area and right padding (equal padding both sides)
  const rightPad = hasIcon ? iconLeftPad : 0;
  const labelOffset = opts.labelOffsetX ?? 0;
  const textX = (hasIcon ? iconEndX + (w - iconEndX - rightPad) / 2 : w / 2) + labelOffset;
  const text = scene.add.text(textX, h / 2, label.toUpperCase(), {
    fontSize: '10px',
    color: textColor,
    fontFamily: Fonts.heading,
    fontStyle: '900',
    letterSpacing: 2,
  });
  text.setOrigin(0.5);
  container.add(text);

  // Hit zone
  const hitZone = scene.add.zone(0, 0, w, h).setOrigin(0, 0);
  hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, w, h), Phaser.Geom.Rectangle.Contains);
  container.add(hitZone);

  container.setDepth(opts.depth ?? Depth.topBar);

  hitZone.on('pointerover', () => {
    bg.clear();
    drawNavRect(bg, 0, 0, w, h, bgHover, 1.0);
    text.setColor(CSSColors.textWhite);
    scene.input.setDefaultCursor('pointer');
  });

  hitZone.on('pointerout', () => {
    bg.clear();
    drawNavRect(bg, 0, 0, w, h, bgColor, 0.85);
    text.setColor(textColor);
    scene.input.setDefaultCursor('default');
  });

  hitZone.on('pointerdown', () => {
    scene.tweens.add({
      targets: container,
      scaleX: 0.93, scaleY: 0.93,
      duration: 50, yoyo: true, ease: 'Quad.easeOut',
    });
    opts.onClick?.();
  });

  return container;
}

/** Draw a sharp-cornered nav button rectangle (radius 0) */
function drawNavRect(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, color: number, alpha: number): void {
  g.fillStyle(color, alpha);
  g.fillRect(x, y, w, h);
}

/** Draw a pill-shaped background (legacy, used by other components) */
export function drawPill(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, color: number, alpha: number): void {
  const r = h / 2;
  g.fillStyle(color, alpha);
  g.fillRoundedRect(x, y, w, h, r);
  // Subtle top highlight
  g.lineStyle(1, 0xffffff, 0.06);
  g.beginPath();
  g.arc(x + r, y + r, r - 0.5, Math.PI, Math.PI * 1.5);
  g.lineTo(x + w - r, y + 0.5);
  g.arc(x + w - r, y + r, r - 0.5, Math.PI * 1.5, Math.PI * 2);
  g.strokePath();
}

/** Internal: draw a button background (normal vs hovered) */
function drawButtonBg(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, hovered: boolean): void {
  const fillColor = hovered ? Colors.bgElevated : Colors.bgPanelLit;
  const fillAlpha = hovered ? 0.9 : 0.7;
  const borderColor = hovered ? Colors.cyan : Colors.borderMed;
  const borderAlpha = hovered ? 0.5 : 0.3;

  g.fillStyle(fillColor, fillAlpha);
  g.fillRoundedRect(x, y, w, h, Radius.md);
  g.lineStyle(1, borderColor, borderAlpha);
  g.strokeRoundedRect(x, y, w, h, Radius.md);

  // Subtle top highlight
  if (hovered) {
    g.lineStyle(1, Colors.cyan, 0.15);
    g.beginPath();
    g.moveTo(x + Radius.md + 2, y);
    g.lineTo(x + w - Radius.md - 2, y);
    g.strokePath();
  }
}

/**
 * Create a styled dialog overlay + background (for modal dialogs)
 * Figma-aligned: gradient fill #0D0D1F -> rgba(26,26,51,0.6),
 * 3px cyan border at 15%, heavy drop shadow.
 * Returns { overlay, bg, closeBtn } added to the container
 */
export function createDialogChrome(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  dialogW: number, dialogH: number,
  opts: { onClose?: () => void; title?: string; titleIcon?: string } = {},
): { overlay: Phaser.GameObjects.Rectangle; closeBtn: Phaser.GameObjects.Container; titleText?: Phaser.GameObjects.Text } {
  const { width, height } = scene.cameras.main;

  // ── Auto-fit: if dialog size exceeds the viewport (minus safe-area and an
  // 8px hairline margin), shrink the whole container uniformly so every
  // button and close-X stays reachable on phones. The caller's layout code
  // is untouched; we just apply a Phaser scale transform.
  const safe = getSafeAreaInsets();
  const fit = resolveDialogSize(dialogW, dialogH, scene, {
    marginTop: Math.max(8, safe.top),
    marginBottom: Math.max(8, safe.bottom),
    marginSide: Math.max(8, Math.max(safe.left, safe.right)),
  });

  if (fit.fitScale < 1) {
    container.setScale(fit.fitScale);
    // Re-center so the scaled dialog sits in the viewport, offset by safe-area
    const scaledW = dialogW * fit.fitScale;
    const scaledH = dialogH * fit.fitScale;
    const newX = Math.round((width - scaledW) / 2);
    const newY = Math.round(
      safe.top + Math.max(0, (height - safe.top - safe.bottom - scaledH) / 2),
    );
    container.setPosition(newX, newY);
  }
  // Remember the "open" scale so closeDialog can animate from the correct base
  container.setData('openScale', fit.fitScale);

  const cx = container.x;
  const cy = container.y;

  // Dark overlay — sized up by 1/fitScale so that after the container's
  // auto-fit transform, the rendered overlay still covers the full viewport.
  const fs = fit.fitScale;
  const overlay = scene.add.rectangle(
    -cx / fs,
    -cy / fs,
    width / fs,
    height / fs,
    0x000000,
    0,
  );
  overlay.setOrigin(0, 0);
  overlay.setInteractive();
  container.add(overlay);

  // Fade overlay in
  scene.tweens.add({ targets: overlay, fillAlpha: 0.65, duration: Anim.dialog, ease: 'Quad.easeOut' });

  // ── Dialog background ────────────────────────────────────
  const bg = scene.add.graphics();

  // Solid fill: #131026
  bg.fillStyle(0x131026, 1);
  bg.fillRect(0, 0, dialogW, dialogH);

  // 3px border: rgba(0,255,255, 0.15)
  bg.lineStyle(3, Colors.cyan, 0.15);
  bg.strokeRect(0, 0, dialogW, dialogH);

  // Header separator line
  bg.lineStyle(1, Colors.cyan, 0.12);
  bg.lineBetween(Space.md, 48, dialogW - Space.md, 48);

  container.add(bg);

  // Invisible hit-zone over the dialog area to absorb clicks
  // so they don't fall through to the overlay and dismiss the dialog
  const dialogHitZone = scene.add.zone(0, 0, dialogW, dialogH).setOrigin(0, 0);
  dialogHitZone.setInteractive(
    new Phaser.Geom.Rectangle(0, 0, dialogW, dialogH),
    Phaser.Geom.Rectangle.Contains,
  );
  container.add(dialogHitZone);

  // Title text (no icon — just plain text, white color)
  let titleText: Phaser.GameObjects.Text | undefined;
  if (opts.title) {
    titleText = scene.add.text(Space.lg, 24, opts.title, {
      fontSize: FontSize.xl,
      color: CSSColors.textWhite,
      fontFamily: Fonts.heading,
      letterSpacing: 1,
    });
    titleText.setOrigin(0, 0.5);
    container.add(titleText);
  }

  // Close button (top-right)
  const closeBtn = scene.add.container(dialogW - 36, 12);
  const closeBg = scene.add.graphics();
  closeBg.fillStyle(0x131026, 0.8);
  closeBg.fillRoundedRect(0, 0, 24, 24, Radius.sm);
  closeBg.lineStyle(1, Colors.cyan, 0.15);
  closeBg.strokeRoundedRect(0, 0, 24, 24, Radius.sm);
  const closeX = scene.add.text(12, 12, '✕', {
    fontSize: FontSize.md,
    color: CSSColors.textMuted,
    fontFamily: Fonts.heading,
  });
  closeX.setOrigin(0.5);
  // Use a Zone for reliable hit detection inside nested containers
  const closeHitZone = scene.add.zone(0, 0, 24, 24).setOrigin(0, 0);
  closeHitZone.setInteractive(
    new Phaser.Geom.Rectangle(0, 0, 24, 24),
    Phaser.Geom.Rectangle.Contains,
  );
  closeBtn.add([closeBg, closeX, closeHitZone]);

  closeHitZone.on('pointerover', () => {
    closeBg.clear();
    closeBg.fillStyle(Colors.red, 0.3);
    closeBg.fillRoundedRect(0, 0, 24, 24, Radius.sm);
    closeBg.lineStyle(1, Colors.red, 0.5);
    closeBg.strokeRoundedRect(0, 0, 24, 24, Radius.sm);
    closeX.setColor(CSSColors.red);
    scene.input.setDefaultCursor('pointer');
  });
  closeHitZone.on('pointerout', () => {
    closeBg.clear();
    closeBg.fillStyle(0x131026, 0.8);
    closeBg.fillRoundedRect(0, 0, 24, 24, Radius.sm);
    closeBg.lineStyle(1, Colors.cyan, 0.15);
    closeBg.strokeRoundedRect(0, 0, 24, 24, Radius.sm);
    closeX.setColor(CSSColors.textMuted);
    scene.input.setDefaultCursor('default');
  });
  closeHitZone.on('pointerdown', () => opts.onClose?.());
  container.add(closeBtn);

  // Scale-in animation for dialog content, respecting any auto-fit scale
  const openScale = fit.fitScale; // final scale after tween
  container.setScale(openScale * 0.92);
  container.setAlpha(0);
  scene.tweens.add({
    targets: container,
    scaleX: openScale, scaleY: openScale, alpha: 1,
    duration: Anim.dialog,
    ease: 'Back.easeOut',
  });

  // ESC key to close
  const escKey = scene.input.keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.ESC, false);
  const escHandler = () => opts.onClose?.();
  escKey?.once('down', escHandler);

  // Click overlay to close (delay 1 frame to avoid capturing the event that opened this dialog)
  scene.time.delayedCall(50, () => {
    overlay.on('pointerdown', () => opts.onClose?.());
  });

  // Clean up ESC listener when dialog is destroyed to avoid leaks
  container.once('destroy', () => {
    escKey?.off('down', escHandler);
  });

  return { overlay, closeBtn, titleText };
}

/**
 * Animate a dialog closing, then call callback
 */
export function closeDialog(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  onComplete?: () => void,
): void {
  const openScale: number = (container.getData('openScale') as number | undefined) ?? 1;
  scene.tweens.add({
    targets: container,
    scaleX: openScale * 0.92,
    scaleY: openScale * 0.92,
    alpha: 0,
    duration: Anim.fast,
    ease: 'Quad.easeIn',
    onComplete: () => {
      container.destroy();
      onComplete?.();
    },
  });
}

/**
 * Draw a horizontal divider line
 */
export function drawDivider(
  g: Phaser.GameObjects.Graphics,
  x: number, y: number, w: number,
): void {
  g.lineStyle(1, Colors.borderDim, 0.5);
  g.lineBetween(x, y, x + w, y);
}

/**
 * Flash a text object (e.g. when resource value changes)
 */
export function flashText(scene: Phaser.Scene, textObj: Phaser.GameObjects.Text, color: string = CSSColors.cyan): void {
  const original = textObj.style.color as string;
  textObj.setColor(color);
  scene.tweens.add({
    targets: textObj,
    alpha: { from: 1, to: 0.5 },
    duration: 100,
    yoyo: true,
    repeat: 1,
    onComplete: () => {
      textObj.setColor(original);
      textObj.setAlpha(1);
    },
  });
}

/**
 * Create a text style config matching theme
 */
export function textStyle(
  size: keyof typeof FontSize,
  color: string = CSSColors.textLight,
  family: 'heading' | 'body' | 'ui' = 'body',
): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontSize: FontSize[size],
    color,
    fontFamily: Fonts[family],
  };
}

/**
 * Create a heading text style
 */
export function headingStyle(size: keyof typeof FontSize = 'xl'): Phaser.Types.GameObjects.Text.TextStyle {
  return {
    fontSize: FontSize[size],
    color: CSSColors.cyan,
    fontFamily: Fonts.heading,
    letterSpacing: 1,
  };
}
